/**
 * shared/address.ts — the ONLY coordinate system, and the ONLY orderer.
 *
 * Everything in this product — highlights, regeneration, bookmarks, co-read positions, cue lists,
 * telemetry, pronunciation overrides — is a claim about WHICH SENTENCE. There is exactly one answer
 * to that question and it is built here.
 *
 *   chapter    c003
 *   block      c003.b0042
 *   sentence   c003.b0042.s2      ← the "sid". This is the atom.
 *   page       c003.p07           ← a canonical page: a server-side run of blocks
 *
 * LAW 1. A sid is POSITIONAL and is only meaningful inside its edition. Every row that stores one and
 * every message that transmits one carries `editionId` beside it. The id string itself deliberately
 * does not: a 40-cue page JSON does not need "ed_7f3a91c2b40e:" repeated forty times, and a DOM id
 * does not need it at all, because a rendered chapter is by construction one edition.
 *
 * LAW 2. Resolving a sid against an edition it did not come from is a bug, not a fallback. It is not
 * an error anyone will see: it silently addresses A DIFFERENT SENTENCE THAT HAPPENS TO EXIST, and the
 * reader lights the wrong line while the audio plays the right one. Remap through `doc_remaps` first,
 * or refuse. `assertSameEdition()` below is the cheap version of that check.
 */

// ─── padding ─────────────────────────────────────────────────────────────────

/**
 * Zero-padding widths, exactly as §2.1 specifies. These are part of the on-disk format: change one
 * and every stored sid, cue list, bookmark, position row and telemetry row in the database addresses
 * a sentence that no longer exists under the new spelling. They are not tunables.
 *
 * What OVERFLOW means, per field:
 *
 *   CHAPTER_PAD = 3   → c000..c999 stay 4 chars. Chapter 1000 becomes "c1000".
 *   BLOCK_PAD   = 4   → b0000..b9999. Block 10000 becomes "b10000". A single-spine Calibre EPUB
 *                       whose chapterisation fails yields ONE chapter holding the whole book, so
 *                       this is reachable in practice, not theoretical.
 *   PAGE_PAD    = 2   → p00..p99, i.e. ~150k chars of chapter at PAGE_TARGET_CHARS. Also reachable
 *                       on the same failure.
 *   SENTENCE_PAD = 0  → the sentence ordinal is NOT padded: §2.1 spells it "s2", not "s002".
 *
 * Overflow does not corrupt anything, because parsing accepts more digits than the pad and because
 * `compareAddress()` compares PARSED INTEGERS. What overflow does break is the incidental property
 * that these strings sort lexicographically into reading order — and the unpadded sentence ordinal
 * breaks it immediately, at "s10" < "s2".
 *
 * That is the whole reason `compareAddress()` exists and is mandatory. Never `localeCompare`, never a
 * raw string sort, never `a.ord - b.ord` on one key alone.
 */
export const CHAPTER_PAD = 3;
export const BLOCK_PAD = 4;
export const PAGE_PAD = 2;
export const SENTENCE_PAD = 0;

// ─── construction ────────────────────────────────────────────────────────────

function ordinal(n: number, what: string): number {
  if (!Number.isSafeInteger(n) || n < 0) {
    // Loud, not lenient. A NaN or a -1 arriving here becomes "cNaN" or "c-01", which parses back as
    // null everywhere downstream and turns into a missing highlight nobody can trace to its source.
    throw new TypeError(`address: ${what} must be a non-negative integer, got ${String(n)}`);
  }
  return n;
}

const pad = (n: number, width: number) => String(n).padStart(width, "0");

/** "c003" */
export function chapterIdOf(chapterOrd: number): string {
  return `c${pad(ordinal(chapterOrd, "chapterOrd"), CHAPTER_PAD)}`;
}

/** "c003.b0042" */
export function blockIdOf(chapterOrd: number, blockOrd: number): string {
  return `${chapterIdOf(chapterOrd)}.b${pad(ordinal(blockOrd, "blockOrd"), BLOCK_PAD)}`;
}

/** "c003.b0042.s2" — the sid. */
export function sidOf(chapterOrd: number, blockOrd: number, sentOrd: number): string {
  return `${blockIdOf(chapterOrd, blockOrd)}.s${pad(ordinal(sentOrd, "sentOrd"), SENTENCE_PAD)}`;
}

/** "c003.p07" — a canonical page, NOT a display column. See `Page` in shared/doc.ts. */
export function pageIdOf(chapterOrd: number, pageOrd: number): string {
  return `${chapterIdOf(chapterOrd)}.p${pad(ordinal(pageOrd, "pageOrd"), PAGE_PAD)}`;
}

// ─── parsing ─────────────────────────────────────────────────────────────────

export type AddressKind = "chapter" | "block" | "sentence" | "page";

export interface Address {
  kind: AddressKind;
  /** The id this was parsed from, verbatim. Kept so comparison has a total-order tiebreak. */
  id: string;
  chapterOrd: number;
  blockOrd: number | null;
  sentOrd: number | null;
  pageOrd: number | null;
}

/** An `Address` narrowed to a sentence, so `blockOrd`/`sentOrd` need no null check at the call site. */
export interface SentenceAddress extends Address {
  kind: "sentence";
  blockOrd: number;
  sentOrd: number;
  pageOrd: null;
}

// `\d{N,}` and not `\d{N}`: see the overflow note above. A parser that rejects "b10000" turns a big
// chapter into a book with no addressable sentences past block 9999.
const RE_CHAPTER = new RegExp(`^c(\\d{${CHAPTER_PAD},})$`);
const RE_BLOCK = new RegExp(`^c(\\d{${CHAPTER_PAD},})\\.b(\\d{${BLOCK_PAD},})$`);
const RE_SENTENCE = new RegExp(`^c(\\d{${CHAPTER_PAD},})\\.b(\\d{${BLOCK_PAD},})\\.s(\\d+)$`);
const RE_PAGE = new RegExp(`^c(\\d{${CHAPTER_PAD},})\\.p(\\d{${PAGE_PAD},})$`);

/** Parse any of the four address forms. Returns null — never throws — so callers can decide. */
export function parseAddress(id: string): Address | null {
  if (typeof id !== "string" || id.length === 0) return null;

  let m = RE_SENTENCE.exec(id);
  if (m) {
    return { kind: "sentence", id, chapterOrd: +m[1]!, blockOrd: +m[2]!, sentOrd: +m[3]!, pageOrd: null };
  }
  m = RE_BLOCK.exec(id);
  if (m) {
    return { kind: "block", id, chapterOrd: +m[1]!, blockOrd: +m[2]!, sentOrd: null, pageOrd: null };
  }
  m = RE_PAGE.exec(id);
  if (m) {
    return { kind: "page", id, chapterOrd: +m[1]!, blockOrd: null, sentOrd: null, pageOrd: +m[2]! };
  }
  m = RE_CHAPTER.exec(id);
  if (m) {
    return { kind: "chapter", id, chapterOrd: +m[1]!, blockOrd: null, sentOrd: null, pageOrd: null };
  }
  return null;
}

/** Parse a SENTENCE address specifically. A block id or a page id returns null, not a partial answer. */
export function parseSid(sid: string): SentenceAddress | null {
  const a = parseAddress(sid);
  return a !== null && a.kind === "sentence" ? (a as SentenceAddress) : null;
}

export const isSid = (id: string): boolean => RE_SENTENCE.test(id);

/** "c003.b0042.s2" → "c003.b0042". The reader uses this to find `[data-b]` from `[data-s]`. */
export function blockIdFrom(id: string): string | null {
  const a = parseAddress(id);
  if (a === null || a.blockOrd === null) return null;
  return blockIdOf(a.chapterOrd, a.blockOrd);
}

/** Any address → its chapter id. */
export function chapterIdFrom(id: string): string | null {
  const a = parseAddress(id);
  return a === null ? null : chapterIdOf(a.chapterOrd);
}

/** Any address → its chapter ordinal. Null when the id does not parse. */
export function chapterOrdFrom(id: string): number | null {
  return parseAddress(id)?.chapterOrd ?? null;
}

// ─── ordering ────────────────────────────────────────────────────────────────

// Blocks/sentences and pages are two different PROJECTIONS of the same chapter and do not interleave
// meaningfully — a page is a run of blocks, so "is c003.p07 before c003.b0042?" has no answer that is
// not a lie. Sorting a mixed list is a bug; this rank only guarantees the sort is deterministic
// instead of depending on the input order, so the bug is reproducible when you go looking for it.
const PROJECTION: Record<AddressKind, number> = { chapter: 0, page: 1, block: 2, sentence: 2 };

/**
 * THE orderer. Not `localeCompare`, not a raw string sort, not a numeric sort on a parsed suffix.
 *
 * Sorts by every ordinal in turn and then by the literal id — the (ord, path) rule. books.karti.ai
 * learned it in `fragments.ts`: a tie on `ord` alone lets two subsystems disagree about which section
 * is which, and neither of them throws. Here the tiebreak also makes this a TOTAL order, so two
 * distinct ids never compare equal and `Array.prototype.sort` cannot reorder them run to run.
 *
 * Throws on an unparseable id. That is deliberate: a comparator that silently sorts garbage to the
 * front produces a book that reads in the wrong order with nothing in any log.
 */
export function compareAddress(a: string, b: string): number {
  const x = parseAddress(a);
  const y = parseAddress(b);
  if (x === null) throw new TypeError(`compareAddress: not an address: ${JSON.stringify(a)}`);
  if (y === null) throw new TypeError(`compareAddress: not an address: ${JSON.stringify(b)}`);

  if (x.chapterOrd !== y.chapterOrd) return x.chapterOrd - y.chapterOrd;

  const px = PROJECTION[x.kind];
  const py = PROJECTION[y.kind];
  if (px !== py) return px - py;

  if (px === 1) return (x.pageOrd as number) - (y.pageOrd as number);

  if (px === 2) {
    if (x.blockOrd !== y.blockOrd) return (x.blockOrd as number) - (y.blockOrd as number);
    // A block id sorts before every sentence inside it: -1 is below sentence ordinal 0.
    const sx = x.sentOrd ?? -1;
    const sy = y.sentOrd ?? -1;
    if (sx !== sy) return sx - sy;
  }

  return x.id < y.id ? -1 : x.id > y.id ? 1 : 0;
}

/** A row that carries its own ordinal — chapters (`ord` or `readOrd`), pages, blocks. */
export interface Ordered { ord: number; id: string }

/**
 * The (ord, path) rule for rows that already carry an explicit ordinal, e.g. sorting chapters by
 * `readOrd` after the structure editor has moved them. Same reason as `compareAddress`: `a.ord -
 * b.ord` alone is not a total order and a tie is silent.
 */
export function compareOrdered(a: Ordered, b: Ordered): number {
  if (a.ord !== b.ord) return a.ord - b.ord;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

// ─── LAW 2, made checkable ───────────────────────────────────────────────────

/**
 * Guard for the moment a sid crosses an edition boundary. Call it wherever a stored `editionId` meets
 * `books.currentEditionId`; if it throws, the correct fix is to migrate the row through `doc_remaps`
 * (§3.6) and write it back — never to reinterpret the sid against the new edition.
 *
 * `what` names the call site and ends up in the message, because "editions differ" with no subject is
 * a bug report nobody can act on.
 */
export function assertSameEdition(expectedEditionId: string, actualEditionId: string, what: string): void {
  if (expectedEditionId !== actualEditionId) {
    throw new Error(
      `${what}: sid is scoped to edition ${actualEditionId} but was resolved against ${expectedEditionId}. ` +
        `Remap through doc_remaps first (LAW 2) or refuse — do not reinterpret.`,
    );
  }
}
